import { APPOINTMENT_STATUS_LABELS, type AppointmentStatus } from '@lia/contracts';
import { Building03Icon, CalendarSetting01Icon, Location01Icon, UserIcon } from '@hugeicons/core-free-icons';
import { Icon } from '../primitives/icon.js';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatLongDate, formatTime, formatTimezone } from '../../lib/format.js';
import { StatusBadge } from '../primitives/badge.js';
import { Card } from '../primitives/card.js';
import { APPOINTMENT_STATUS_TONE } from './status.js';

export interface AppointmentCardProps {
  startsAt: string;
  endsAt?: string | null;
  timezone: string;
  status: AppointmentStatus;
  contactName?: string;
  projectName: string;
  /** null = not assigned yet. */
  advisorName: string | null;
  location?: string | null;
  /** Calendly reschedule link; null/undefined = not available for this booking. */
  rescheduleUrl?: string | null;
  cancelUrl?: string | null;
  actions?: React.ReactNode;
  className?: string;
}

function LinkOrUnavailable({ href, label }: { href?: string | null; label: string }) {
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className="font-medium text-brand-primary underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-focus">
        {label}
      </a>
    );
  }
  return <span className="text-text-secondary">{label}: no disponible</span>;
}

export const AppointmentCard = React.forwardRef<HTMLDivElement, AppointmentCardProps>(function AppointmentCard(
  { startsAt, endsAt, timezone, status, contactName, projectName, advisorName, location, rescheduleUrl, cancelUrl, actions, className },
  ref,
) {
  const range = endsAt ? `${formatTime(startsAt, timezone)} – ${formatTime(endsAt, timezone)}` : formatTime(startsAt, timezone);
  const inactive = status === 'cancelled' || status === 'completed' || status === 'no_show';
  return (
    <Card ref={ref} as="article" className={cn('grid gap-3 p-4', className)}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-start gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-control bg-accent-selected text-accent-on-selected">
            <Icon icon={CalendarSetting01Icon} aria-hidden className="size-5" />
          </span>
          <div className="grid gap-0.5">
            <p className="text-body font-semibold first-letter:uppercase text-text-primary">{formatLongDate(startsAt, timezone)}</p>
            <p className="ef-tabular text-body text-text-primary">{range}</p>
            <p className="text-meta text-text-secondary">{formatTimezone(timezone, startsAt)}</p>
          </div>
        </div>
        <StatusBadge tone={APPOINTMENT_STATUS_TONE[status]}>{APPOINTMENT_STATUS_LABELS[status]}</StatusBadge>
      </div>
      <dl className="grid gap-1.5 text-body [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-text-secondary">
        {contactName ? (
          <div className="flex items-center gap-2">
            <dt className="sr-only">Contacto</dt>
            <Icon icon={UserIcon} aria-hidden />
            <dd className="font-medium text-text-primary">{contactName}</dd>
          </div>
        ) : null}
        <div className="flex items-center gap-2">
          <dt className="sr-only">Proyecto</dt>
          <Icon icon={Building03Icon} aria-hidden />
          <dd className="text-text-primary">{projectName}</dd>
        </div>
        <div className="flex items-center gap-2">
          <dt className="text-text-secondary">Asesor:</dt>
          <dd className={advisorName ? 'text-text-primary' : 'italic text-text-secondary'}>{advisorName ?? 'Sin asignar'}</dd>
        </div>
        {location ? (
          <div className="flex items-center gap-2">
            <dt className="sr-only">Lugar</dt>
            <Icon icon={Location01Icon} aria-hidden />
            <dd className="text-text-primary">{location}</dd>
          </div>
        ) : null}
      </dl>
      {!inactive ? (
        <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-border-subtle pt-3 text-meta">
          <LinkOrUnavailable href={rescheduleUrl} label="Reprogramar" />
          <LinkOrUnavailable href={cancelUrl} label="Cancelar cita" />
        </div>
      ) : null}
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </Card>
  );
});
